/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import TableContainer from '@mui/material/TableContainer';
import { Box } from '@mui/material';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

import Swal from 'sweetalert2';
import PosPrintClose from './PosPrintClose';
import PosPrintCloseSum from './PosPrintCloseSum';
import CashInfo from './CashInfo';
import CreditInfo from './CreditInfo';
import BankInfo from './BankInfo';
import PosPrintCloseSend from './PosPrintCloseSend';

function initStepper({ icon, completed }) {
  const icons_complete = {
    1: <Icon color='info'>qr_code_scanner</Icon>,
    2: <Icon color='info'>money</Icon>,
    3: <Icon color='info'>camera_alt</Icon>,
    4: <Icon color='info'>price_change</Icon>,
    5: <Icon color='info'>article</Icon>,
    6: <Icon color='info'>task_alt</Icon>,
  };
  const icons = {
    1: <Icon color='disabled'>qr_code_scanner</Icon>,
    2: <Icon color='disabled'>money</Icon>,
    3: <Icon color='disabled'>camera_alt</Icon>,
    4: <Icon color='disabled'>price_change</Icon>,
    5: <Icon color='disabled'>article</Icon>,
    6: <Icon color='disabled'>task_alt</Icon>,
  };
  return <div>{completed ? icons_complete[String(icon)] : icons[String(icon)]}</div>;
}

const stepperName = {
  edit: {
    name: 'สแกน',
    color: 'warning',
    icon: 'qr_code_scanner',
  },
  counting: {
    name: 'นับเงิน',
    color: 'info',
    icon: 'hourglass_top',
  },
  photo: {
    name: 'ถ่ายภาพ',
    color: 'info',
    icon: 'camera_alt',
  },
  summary: {
    name: 'ยอดนำส่ง',
    color: 'info',
    icon: 'price_change',
  },
  save: {
    name: 'บันทึกข้อมูล',
    color: 'info',
    icon: 'price_change',
  },
  sales_form: {
    name: 'ส่งยอดขาย',
    color: 'info',
    icon: 'article',
  },
  success: {
    name: 'เสร็จสิ้น',
    color: 'success',
    icon: 'task_alt',
  },
};

const initAttr = {
  id: '',
  name: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initSum = {
  count_all: 0,
  first_bill: '0',
  last_bill: '0',
  discount_coupon: 0,
  discount_approve: 0,
  discount_return: 0,
  discount_promotion: 0,
  discount_all: 0,
  total: 0,
  nettotal: 0,
  money_cash: 0,
  money_transfer: 0,
  credit_visa: 0,
  credit_mastercard: 0,
  credit_alipay: 0,
  credit_wechat: 0,
  credit_jcb: 0,
  credit_tpn: 0,
  credit_upay: 0,
  money_send: 0,
  wallet_diff: 0,
  credit_diff: 0,
};

const initRequest = {
  request: false,
  approve: false,
  status: '',
};

function PosCloseShift({ reload, setBlockScan, setMenu }) {
  const mod = 'pos_closeshift';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [sum, setSum] = useState(initSum);
  const [record, setRecord] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [notify, setNotify] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  const [imageScan, setImageScan] = useState('');
  const [imageQR, setImageQR] = useState('');
  const [imageQrPhoto, setImageQrPhoto] = useState('');
  const [image, setImage] = useState([]);
  const [cboCash, setCboCash] = useState([]);
  const [cboCredit, setCboCredit] = useState([]);
  const [cboBank, setCboBank] = useState([]);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState('');
  const [credit, setCredit] = useState([]);
  const [totalCredit, setTotalCredit] = useState('');
  const [transfer, setTransfer] = useState([]);
  const [totalTransfer, setTotalTransfer] = useState('');
  const [cancels, setCancel] = useState([]);
  const [moneyReceive, setMoneyReceive] = useState('');
  const [moneyBalance, setMoneyBalance] = useState('');
  const printRef = useRef();
  const printSum = useRef();
  const [request, setRequest] = useState(initRequest);
  const [token, setToken] = useState('');

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );

    setTimeout(function () {
      setNotify(null);
    }, 5000);
  };

  const calculateMoney = () => {
    const send = sum.total_cash * 1;
    const receive = moneyReceive * 1;
    const balance = send - receive;

    if (balance < 0) {
      setMoneyReceive('');
      setMoneyBalance('0.00');
    } else {
      setMoneyBalance(balance);
    }
  };

  const close = () => {
    if (activeStep > 2) {
      return;
    }
    setModalAdd(false);
    setBlockScan(false);
  };

  const view = () => {
    setLoading(true);
    setBlockScan(true);
    setActiveStep(1);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
    })
      .then((result) => {
        if (result.status) {
          if (result.status === 'ok') {
            setAttr(result.data);
            setCboCash(result.cash);
            setCboCredit(result.creditcard);
            setCboBank(result.bank);
            setImageScan(result.mobile);
            setImageQR(result.qrcode);
            setImageQrPhoto(result.qrphoto);
            setImage(result.image);
            setModalAdd(true);
          } else if (result.status === 'not_close_time') {
            Swal.fire({
              icon: 'error',
              title: 'ยังไม่ถึงเวลาปิดรอบ',
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: result.msg,
            });
          }
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          Swal.fire({
            icon: 'error',
            title: result.msg,
          });
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const editTotalCash = (row) => {
    const data = [];
    let result = 0;

    list.map((item) => (row.id === item.id ? null : data.push(item)));

    data.push(row);
    setList(data);

    data.forEach(function (item) {
      result += item.total * 1;
    });

    setTotal(result.toString());
  };

  const editTotalTransfer = (row) => {
    const data = [];
    let result = 0;

    transfer.map((item) => (row.id === item.id ? null : data.push(item)));

    data.push(row);
    setTransfer(data);

    data.forEach(function (item) {
      result += item.total * 1;
    });

    setTotalTransfer(result.toString());
  };

  const editTotalCredit = (row) => {
    const data = [];
    let result = 0;

    credit.map((item) => (row.id === item.id ? null : data.push(item)));

    data.push(row);
    setCredit(data);

    data.forEach(function (item) {
      result += item.total * 1;
    });

    setTotalCredit(result.toString());
  };

  const requestCloseShift = () => {
    FetchData('OPTIONS', `/${mod}/request`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setRequest({
            request: true,
            approve: false,
            status: '',
          });
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  const scan = () => {
    setTotal('');
    setTotalCredit('');
    setTotalTransfer('');
    setList([]);
    setCredit([]);
    setTransfer([]);

    FetchData('OPTIONS', `/${mod}/scan`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          requestCloseShift();
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: result.msg,
          });
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  const refresh = () => {
    FetchData('OPTIONS', `/${mod}/refresh`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          if (result.msg === 'approved') {
            printRef.current.print();
            setActiveStep(activeStep + 1);
            setRequest({
              request: false,
              approve: true,
              status: 'approved',
            });
          } else if (result.msg === 'requesting') {
            setRequest({
              request: true,
              approve: false,
              status: 'requesting',
            });
          } else if (result.msg === 'rejected') {
            setRequest({
              request: false,
              approve: false,
              status: 'rejected',
            });
          }
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  const counting = () => {
    FetchData('OPTIONS', `/${mod}/counting`, app.token, {
      list: list,
      credit: credit,
      transfer: transfer,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setActiveStep(activeStep + 1);
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  const summary = () => {
    FetchData('OPTIONS', `/${mod}/summary`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setAttr(result.data);
          setRecord(result.list);
          setImage(result.image);
          setSum(result.summary);
          calculateMoney();
          setActiveStep(activeStep + 1);
          setMoneyReceive('');
          setMoneyBalance(result.summary.total_cash);
          // printSum.current.print();
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  const success = () => {
    // console.log('success');
    FetchData('OPTIONS', `/${mod}/success`, app.token, {})
      .then((result) => {
        if (result.status && result.status === 'ok') {
          setActiveStep(activeStep + 1);
          localStorage.removeItem('pos_status');
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
          Swal.fire({
            icon: 'error',
            text: result.msg,
          });
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  const salesForm = () => {
    FetchData('OPTIONS', `/${mod}/sales_form`, app.token, {
      money_receive: moneyReceive,
      money_balance: moneyBalance,
    })
      .then((result) => {
        if (result.status && result.status === 'ok') {
          printSum.current.print();
          setActiveStep(activeStep + 1);
          setToken(result.token);
          // success();
        } else if (!result.status) {
          showNotify(t('alert.server_timeout'), 'error');
        } else {
          showNotify(result.msg, 'error');
        }
      })
      .catch((error) => {
        showNotify(t('alert.network_timeout'), 'error');
      });
  };

  const handleNext = () => {
    if (activeStep === 1 && !request.request) {
      if (!request.approve) {
        scan();
        // setActiveStep(activeStep + 1);
      } else {
        setActiveStep(activeStep + 1);
      }
    } else if (activeStep === 2) {
      counting();
    } else if (activeStep === 3) {
      summary();
    } else if (activeStep === 4) {
      salesForm();
    } else if (activeStep === 5) {
      success();
    } else if (activeStep === 6) {
      window.location.reload();
    }
  };

  const handleBack = () => {
    // console.log(activeStep);
    if (activeStep === 3) {
      setTotal('');
      setTotalCredit('');
      setList([]);
      setCredit([]);
    }

    setActiveStep(activeStep - 1);
  };

  const renderStepper = () => {
    const stepLevel = (key) => {
      return (
        <Step>
          <StepLabel StepIconComponent={initStepper}>
            <MDTypography variant='subtitle2'>{stepperName[key].name}</MDTypography>
          </StepLabel>
        </Step>
      );
    };

    return (
      <MDBox className='stepper' mt={3} mb={1}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {stepLevel('edit')}
          {stepLevel('counting')}
          {stepLevel('photo')}
          {stepLevel('summary')}
          {stepLevel('sales_form')}
          {stepLevel('success')}
        </Stepper>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2} lg={1}>
            &nbsp;
          </Grid>
          <Grid item xs={12} md={3} lg={4}>
            <MDBox className='card' p={2}>
              <MDBox>
                <img src={imageQrPhoto} alt='' width='100%' />
              </MDBox>
              <MDBox align='center'>
                <MDTypography>SCAN QR CODE</MDTypography>
              </MDBox>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={1} lg={1}>
            &nbsp;
          </Grid>
          <Grid item xs={12} md={4} lg={4}>
            <img src={imageScan} alt='' width='100%' />
          </Grid>
          <Grid item xs={12} md={2} lg={1}>
            &nbsp;
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderScan = () => {
    return request.request || request.status === 'rejected' ? (
      <MDBox mt={3}>
        <Grid container spacing={1}>
          <Grid item xs={12} align='center'>
            <Alert
              variant='outlined'
              severity='warning'
              action={
                request.status === 'rejected' ? (
                  <MDButton size='small' onClick={close}>
                    <Icon>close</Icon> Close
                  </MDButton>
                ) : (
                  <MDButton size='small' onClick={refresh}>
                    <Icon>refresh</Icon> Refresh
                  </MDButton>
                )
              }
            >
              {request.status === 'requesting'
                ? 'ยังไม่ได้รับการอนุมัติ'
                : request.status === 'rejected'
                ? 'การปิดรอบของคุณถูกปฏิเสธ'
                : 'รอการอนุมัติ'}
            </Alert>
          </Grid>
        </Grid>
      </MDBox>
    ) : (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2} lg={1} />
          <Grid item xs={12} md={3} lg={4}>
            <MDBox className='card' p={2}>
              <MDBox>
                <img src={imageQR} alt='' width='100%' />
              </MDBox>
              <MDBox align='center'>
                <MDTypography>SCAN QR CODE</MDTypography>
              </MDBox>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={1} lg={1} />
          <Grid item xs={12} md={4} lg={4}>
            <img src={imageScan} alt='' width='100%' />
          </Grid>
          <Grid item xs={12} md={2} lg={1} />
        </Grid>
      </MDBox>
    );
  };

  const renderImage = () => {
    if (image.length === 0) return null;

    return (
      <MDBox className='card' p={2} mb={2}>
        <MDBox align='center' mb={2}>
          <MDTypography variant='h5'>ภาพประกอบ</MDTypography>
        </MDBox>
        <Grid container spacing={2}>
          {image.map((item) => (
            <Grid item xs={12} md={12} key={item.id}>
              <MDBox sx={{ borderRadius: '5px', overflow: 'hidden' }}>
                <img src={item.image} alt={item.title} width='100%' />
              </MDBox>
            </Grid>
          ))}
        </Grid>
      </MDBox>
    );
  };

  const renderTopic = (title) => {
    return (
      <Grid item xs={12}>
        <MDTypography variant='body' className='underline'>
          {title}
        </MDTypography>
      </Grid>
    );
  };

  const renderNumber = (title, value, line = false) => {
    return (
      <>
        <Grid item xs={6}>
          {title}
        </Grid>
        <Grid item xs={6} align='right' className={line ? 'underline' : ''}>
          {Func.numberDisplay(value, 2)}
        </Grid>
      </>
    );
  };

  const renderCancel = () => {
    return cancels.map((row) => (
      <Grid container key={row.id}>
        <Grid item xs={6}>
          {row.code} {row.date_create}
        </Grid>
        <Grid item xs={6} align='right'>
          {Func.numberDisplay(row.nettotal, 2)}
        </Grid>
      </Grid>
    ));
  };

  const txtMoneySend = () => {
    return (
      <>
        <Grid item xs={6}>
          <MDTypography variant='body' color='error'>
            นำส่งเงิน
          </MDTypography>
        </Grid>
        <Grid item xs={6} align='right'>
          <MDBox
            noValidate
            component='form'
            role='form'
            onSubmit={(e) => {
              e.preventDefault();

              calculateMoney();
            }}
          >
            <MDInput
              onChange={(e) => setAttr({ ...attr, money_send: e.target.value })}
              onFocus={(e) => e.target.select()}
              onBlur={calculateMoney}
              value={attr.money_send}
              variant='standard'
              type='number'
              inputProps={{
                autoFocus: true,
                maxLength: 20,
                autoComplete: 'off',
                className: 'text-right',
                placeholder: '0.00',
              }}
              fullWidth
            />
          </MDBox>
        </Grid>
      </>
    );
  };

  const txtMoneyReceive = () => {
    return (
      <>
        <Grid item xs={6}>
          <MDTypography variant='body' color='error'>
            รับเงินเพื่อทอน
          </MDTypography>
        </Grid>
        <Grid item xs={6} align='right'>
          <MDBox
            noValidate
            component='form'
            role='form'
            onSubmit={(e) => {
              e.preventDefault();

              calculateMoney();
            }}
          >
            <MDInput
              onChange={(e) => setMoneyReceive(e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={calculateMoney}
              value={moneyReceive}
              variant='standard'
              type='number'
              inputProps={{
                autoFocus: true,
                maxLength: 20,
                autoComplete: 'off',
                className: 'text-right',
                placeholder: '0.00',
              }}
              fullWidth
            />
          </MDBox>
        </Grid>
      </>
    );
  };

  const renderSlip = () => {
    return (
      <MDBox className='card' p={2}>
        <MDBox align='center' mb={2}>
          <MDTypography variant='h5'>ใบปิดรอบ</MDTypography>
        </MDBox>
        <Grid container>
          <Grid item xs={12} align='center'>
            <MDTypography variant='body2'>{attr.branch_name}</MDTypography>
          </Grid>
          <Grid item xs={12}>
            &nbsp;
          </Grid>
          <Grid item xs={12}>
            <MDTypography variant='body' className='underline'>
              รายการขาย
            </MDTypography>
          </Grid>
          <Grid item xs={3}>
            บิลแรก
          </Grid>
          <Grid item xs={9} align='right'>
            {sum.first_bill}
            <br /> {Func.dateTimeDisplay(attr.first_date)}
          </Grid>
          <Grid item xs={3}>
            สุดท้าย
          </Grid>
          <Grid item xs={9} align='right'>
            {sum.last_bill}
            <br /> {Func.dateTimeDisplay(attr.last_date)}
          </Grid>
          <Grid item xs={12}>
            &nbsp;
          </Grid>
          <Grid item xs={12}>
            <MDTypography variant='body'>* * บิลที่ปิดเรียบร้อย</MDTypography>
          </Grid>
          <Grid item xs={6}>
            จำนวนบิล
          </Grid>
          <Grid item xs={6} align='right'>
            {Func.numberDisplay(sum.count_all)}
          </Grid>
          {renderNumber('ยอดขายก่อนลด', sum.total)}
          {renderNumber('@ลดตามจำนวน', sum.discount, true)}
          {renderNumber('หลังลดตามจำนวน', 0)}
          {renderNumber('แลกซื้อเมื่อจำนวนครบ', 0, true)}
          <Grid item xs={12}>
            &nbsp;
          </Grid>
          {renderNumber('หลังลดการตลาด', 0, true)}
          <Grid item xs={12}>
            &nbsp;
          </Grid>
          {renderNumber('ยอดรวม', sum.nettotal)}
          {renderNumber('ยอดขายไม่มีภพ.', sum.total_novat)}
          {renderNumber('ยอดขายมีภพ.', sum.total_invat)}
          {renderNumber('ยอดภพ.', sum.vat, true)}
          <Grid item xs={12}>
            &nbsp;
          </Grid>
          {renderTopic('รายการชำระ')}
          {renderNumber('เงินสด', sum.money_cash)}
          {renderNumber('Master Card', sum.credit_mastercard)}
          {renderNumber('VISA Card', sum.credit_visa)}
          {renderNumber('ALIPAY', sum.credit_alipay)}
          {renderNumber('WECHAT', sum.credit_wechat)}
          {renderNumber('JCB', sum.credit_jcb)}
          {renderNumber('Thai payment', sum.credit_tpn)}
          {renderNumber('Union pay', sum.credit_upay)}
          {renderNumber('THAI QR', sum.money_transfer)}
          {renderNumber('ยอดชำระ', sum.nettotal)}
          {renderNumber('ส่วนต่างลิ้นชัก', sum.wallet_diff)}
          {renderNumber('ส่วนต่างบัตร', sum.credit_diff)}
          <Grid item xs={12}>
            &nbsp;
          </Grid>
          {renderTopic('รายการเงินสด')}
          {renderNumber('เงินสด', sum.total_cash)}
          {renderNumber('นำส่งเงิน', sum.total_cash)}
          {txtMoneyReceive()}
          {renderNumber('ยอดต้องนำส่ง', moneyBalance, true)}
          <Grid item xs={12}>
            &nbsp;
          </Grid>
          <Grid item xs={12}>
            <MDTypography variant='body'>* * บิลที่มีรายการยกเลิกหลังรวมเงิน</MDTypography>
          </Grid>
          <Grid item xs={12}>
            {renderCancel()}
          </Grid>
          <Grid item xs={12}>
            <MDTypography variant='body'>* * บิลที่มีรายการยกเลิกระหว่างขาย</MDTypography>
          </Grid>
          <Grid item xs={12}>
            {renderCancel()}
          </Grid>
          <Grid item xs={12}>
            &nbsp;
          </Grid>
          <Grid item xs={12}>
            <MDTypography variant='body'>Cashier : {attr.emp_name}</MDTypography>
          </Grid>
          <Grid item xs={12}>
            <MDTypography variant='body'>{Func.dateTimeDisplay(attr.date_now)}</MDTypography>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <MDBox className='card' p={2}>
        <MDBox align='center' mb={2}>
          <MDTypography variant='h5'>ส่งยอด</MDTypography>
        </MDBox>
        <Grid container>
          <Grid item xs={3}>
            บิลแรก
          </Grid>
          <Grid item xs={9} align='right'>
            {sum.first_bill}
            <br /> {Func.dateTimeDisplay(attr.first_date)}
          </Grid>
          <Grid item xs={3}>
            สุดท้าย
          </Grid>
          <Grid item xs={9} align='right'>
            {sum.last_bill}
            <br /> {Func.dateTimeDisplay(attr.last_date)}
          </Grid>
          <Grid item xs={12}>
            &nbsp;
          </Grid>
          <Grid item xs={6}>
            จำนวนบิล
          </Grid>
          <Grid item xs={6} align='right'>
            {Func.numberDisplay(sum.count_all)}
          </Grid>
          {renderNumber('ยอดโอนเงิน', sum.total_transfer)}
          {renderNumber('ยอดบัตรเครดิต', sum.total_credit)}
          <Grid item xs={12}>
            &nbsp;
          </Grid>
          {renderNumber('นำส่งเงินสด', sum.total_cash)}
          {txtMoneyReceive()}
          {renderNumber('ยอดต้องนำส่ง', moneyBalance, true)}
          <Grid item xs={12}>
            &nbsp;
          </Grid>
          <Grid item xs={6}>
            <MDTypography variant='body'>Cashier : {attr.user_name}</MDTypography>
          </Grid>
          <Grid item xs={6} align='right'>
            <MDTypography variant='body'>{Func.dateTimeDisplay(attr.date_now)}</MDTypography>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderSummary = () => {
    return (
      <MDBox>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            {renderInfo()}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderImage()}
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderCount = () => {
    return (
      <MDBox mt={1}>
        <MDBox>
          <MDBox>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <MDBox className='card'>
                  <TableContainer sx={{ boxShadow: 0 }}>
                    <Table aria-label='simple table'>
                      <TableHead>
                        <TableRow>
                          <TableCell align='right' width={40}>
                            &nbsp;
                          </TableCell>
                          <TableCell align='right' width={90}>
                            เงินสด
                          </TableCell>
                          <TableCell align='center'>จำนวน</TableCell>
                          <TableCell align='right' width={110}>
                            รวม
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cboCash.map((row) => (
                          <CashInfo parent={row} callback={editTotalCash} key={row.id} />
                        ))}
                        <TableRow>
                          <TableCell align='right' colSpan={2}>
                            <MDTypography variant='h4'>รวม</MDTypography>
                          </TableCell>
                          <TableCell align='right' className='color-danger' colSpan={2}>
                            <MDTypography variant='h4'>{Func.numberDisplay(total, 2)}</MDTypography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <MDBox className='card'>
                  <TableContainer sx={{ boxShadow: 0 }}>
                    <Table aria-label='simple table'>
                      <TableHead>
                        <TableRow>
                          <TableCell align='right' width={40}>
                            &nbsp;
                          </TableCell>
                          <TableCell align='left' width={120}>
                            ธนาคาร
                          </TableCell>
                          <TableCell align='center'>จำนวนเงิน</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cboBank.map((row) => (
                          <BankInfo parent={row} callback={editTotalTransfer} key={row.id} />
                        ))}
                        <TableRow>
                          <TableCell align='right' colSpan={2}>
                            <MDTypography variant='h4'>รวม</MDTypography>
                          </TableCell>
                          <TableCell align='right' className='color-danger'>
                            <MDTypography variant='h4'>
                              {Func.numberDisplay(totalTransfer, 2)}
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </MDBox>
                <MDBox className='card' mt={1}>
                  <TableContainer sx={{ boxShadow: 0 }}>
                    <Table aria-label='simple table'>
                      <TableHead>
                        <TableRow>
                          <TableCell align='right' width={40}>
                            &nbsp;
                          </TableCell>
                          <TableCell align='left' width={120}>
                            บัตร
                          </TableCell>
                          <TableCell align='center'>จำนวนเงิน</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cboCredit.map((row) => (
                          <CreditInfo parent={row} callback={editTotalCredit} key={row.id} />
                        ))}
                        <TableRow>
                          <TableCell align='right' colSpan={2}>
                            <MDTypography variant='h4'>รวม</MDTypography>
                          </TableCell>
                          <TableCell align='right' className='color-danger'>
                            <MDTypography variant='h4'>
                              {Func.numberDisplay(totalCredit, 2)}
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
      </MDBox>
    );
  };

  const renderSalesForm = () => {
    return (
      <MDBox mt={3}>
        <Grid container spacing={1}>
          <Grid item xs={12} align='center'>
            {token === 'success' ? (
              <Alert severity='success'>คุณได้ส่งยอดขายแล้ว</Alert>
            ) : (
              <>
                <Box
                  sx={{
                    aspectRatio: '1 / 1',
                    width: 300,
                    p: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=https://form.ruangthongpharmacy.com/close_shift?token=${token}`}
                    alt='description'
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                </Box>
                <MDTypography variant='h6'>สแกน QR Code เพื่อกรอกฟอร์มส่งยอดขาย</MDTypography>
              </>
            )}
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderFinish = () => {
    return (
      <MDBox mt={3}>
        <Grid container spacing={1}>
          <Grid item xs={12} align='center'>
            <Alert severity='success'>บันทึกข้อมูลเรียบร้อยแล้ว</Alert>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderStep = () => {
    if (activeStep === 1) return renderScan();
    if (activeStep === 2) return renderCount();
    if (activeStep === 3) return renderPhoto();
    if (activeStep === 4) return renderSummary();
    if (activeStep === 5) return renderSalesForm();
    if (activeStep === 6) return renderFinish();

    return null;
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='md' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          <i className='fad fa-power-off color-danger' /> &nbsp; ปิดรอบ
          {activeStep < 2 ? (
            <IconButton
              aria-label='close'
              onClick={close}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          ) : null}
        </DialogTitle>
        <DialogContent dividers>
          <MDBox>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {renderStepper()}
              </Grid>
              <Grid item xs={12}>
                <MDBox height={420}>{renderStep()}</MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item xs={6}>
              &nbsp;
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              {activeStep === 6 ? (
                <MDButton variant='text' color='dark' onClick={() => window.location.reload()}>
                  <Icon>close</Icon> ปิด &nbsp;
                </MDButton>
              ) : (
                <MDButton
                  variant='text'
                  color='info'
                  disabled={
                    activeStep > 5 ||
                    (!request.approve && request.request) ||
                    request.status === 'rejected'
                  }
                  onClick={handleNext}
                >
                  ถัดไป &nbsp; <Icon>chevron_right</Icon>
                </MDButton>
              )}
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      <MDBox>
        <MenuItem
          onClick={() => {
            setMenu(null);
            setRequest(initRequest);
            view();
          }}
        >
          <Typography textAlign='center'>
            <i className='fad fa-power-off color-danger' /> ปิดรอบการขาย
          </Typography>
        </MenuItem>
        <PosPrintClose ref={printRef} parent={attr} />
        {/* <PosPrintCloseSum ref={printSum} parent={attr} /> */}
        <PosPrintCloseSend ref={printSum} parent={attr} />

        {renderAdd()}
      </MDBox>
      {notify}
    </>
  );
}

PosCloseShift.propTypes = {
  reload: PropTypes.func.isRequired,
  setBlockScan: PropTypes.func.isRequired,
  setMenu: PropTypes.func.isRequired,
};

export default PosCloseShift;
